import { useState, useEffect, useRef, useCallback } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { parseItemDate } from "../utils/dateUtils";

export const DEFAULT_REMINDER_SETTINGS = {
  bottle:        { enabled: false, intervalMinutes: 180 },
  breastfeeding: { enabled: false, intervalMinutes: 150 },
  diaper:        { enabled: false, intervalMinutes: 120 },
};

const REMINDER_CONFIG = {
  bottle:        { title: "🍼 Feeding Reminder",  body: "Time for a bottle feed!" },
  breastfeeding: { title: "🤱 Feeding Reminder",  body: "Time for a breastfeeding session!" },
  diaper:        { title: "🧷 Nappy Check",        body: "Time to check the nappy!" },
};

const getLatestDate = (items) => {
  if (!items || items.length === 0) return null;
  let latest = null;
  for (const item of items) {
    const d = parseItemDate(item);
    if (d && (!latest || d > latest)) latest = d;
  }
  return latest;
};

const showNotification = (key, title, body) => {
  if (!("Notification" in window) || Notification.permission !== "granted") return;

  const options = {
    body,
    icon:     "/logo192.png",
    badge:    "/logo192.png",
    tag:      `baby-tracker-${key}`,
    renotify: true,
  };

  if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.ready.then((reg) => reg.showNotification(title, options));
  } else {
    // Fallback for when the service worker isn't controlling yet (development)
    new Notification(title, options); // eslint-disable-line no-new
  }
};

export const useReminders = (childId, dataMap) => {
  const { bottleData, breastfeedingData, diaperData } = dataMap || {};

  const [settings, setSettings]   = useState(DEFAULT_REMINDER_SETTINGS);
  const [permission, setPermission] = useState(
    "Notification" in window ? Notification.permission : "unsupported"
  );
  const [loading, setLoading]     = useState(true);
  const timersRef                 = useRef({});

  // Load settings from Firestore
  useEffect(() => {
    if (!childId) { setLoading(false); return; }
    getDoc(doc(db, "reminderSettings", childId)).then((snap) => {
      if (snap.exists()) {
        setSettings((prev) => ({ ...prev, ...snap.data() }));
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [childId]);

  // Persist settings to Firestore
  const saveSettings = useCallback(async (next) => {
    setSettings(next);
    if (childId) {
      await setDoc(doc(db, "reminderSettings", childId), next);
    }
  }, [childId]);

  // Request browser notification permission
  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) return "unsupported";
    const result = await Notification.requestPermission();
    setPermission(result);
    return result;
  }, []);

  // (Re-)schedule timers whenever data or settings change
  useEffect(() => {
    if (permission !== "granted") return;

    const dataByKey = {
      bottle:        bottleData,
      breastfeeding: breastfeedingData,
      diaper:        diaperData,
    };

    const now = Date.now();

    Object.entries(REMINDER_CONFIG).forEach(([key, { title, body }]) => {
      // Always clear the existing timer for this key first
      clearTimeout(timersRef.current[key]);
      delete timersRef.current[key];

      const cfg = settings[key];
      if (!cfg?.enabled) return;

      const latest = getLatestDate(dataByKey[key]);
      if (!latest) return;

      const dueAt   = latest.getTime() + cfg.intervalMinutes * 60 * 1000;
      const delayMs = dueAt - now;

      if (delayMs <= 0) {
        // Already overdue — fire immediately
        showNotification(key, title, body);
      } else {
        timersRef.current[key] = setTimeout(() => showNotification(key, title, body), delayMs);
      }
    });
  }, [settings, permission, bottleData, breastfeedingData, diaperData]);

  // Cleanup timers on unmount
  useEffect(() => {
    const timers = timersRef.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);

  return { settings, saveSettings, permission, requestPermission, loading };
};
